import 'twin.macro';

import { LockClosed } from '@cheqroom/icons';
import { Banner, Button, Form, Icon, Input, Link as LinkUI, Stack, Text, TextStyle } from '@cheqroom/ui';
import { joiResolver } from '@hookform/resolvers/joi';
import type { TFunction } from 'i18next';
import { FC, FormEvent, MouseEvent, ReactNode, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Trans } from 'react-i18next';

import type { AuthenticateResponse } from '../../auth/api/authenticate';
import { useAuthenticateMutation } from '../../auth/hooks/useAuthenticateMutation';
import { toPrivacyPolicyPage, toTermsOfUsePage } from '../EmailStep/EmailStep';
import { type PythonErrorLike } from './get-python-error-key';
import { type PasswordFormData, passwordFormSchema } from './validation';

/** Token shape passed to onSuccess after successful auth. */
export interface AuthTokens {
	access_token: string;
	refresh_token: string;
}

export interface UsernamePasswordFormProps {
	/** Token endpoint URL (e.g. AUTH_TOKEN_URL). Form calls doAuthenticate internally. */
	tokenUrl: string;
	workspaceId: string;
	/** Called with tokens on successful auth. */
	onSuccess: (tokens: AuthTokens) => void;
	forgotPasswordHref: string;
	/** When provided, forgot link validates email then calls this with current email. */
	forgotPasswordOnClick?: (email: string) => void;
	/** Maps API error to display message (e.g. t('namespace:errors.' + getPythonErrorKey(err))). */
	getErrorMessage: (error: PythonErrorLike | null) => string;
	t: TFunction;
	defaultEmail?: string;
	showEmailField?: boolean;
	renderAfterSubmit?: ReactNode;
}

export const UsernamePasswordForm: FC<UsernamePasswordFormProps> = ({
	tokenUrl,
	workspaceId,
	onSuccess,
	forgotPasswordHref,
	forgotPasswordOnClick,
	getErrorMessage,
	t,
	defaultEmail = '',
	showEmailField = true,
	renderAfterSubmit,
}) => {
	const mutation = useAuthenticateMutation(tokenUrl);
	const submitting = mutation.isLoading;

	const acceptTermsBlock = useMemo(
		() => (
			<Text size="small">
				<Trans
					t={t}
					i18nKey="email_step.accept_terms"
					components={[
						<LinkUI key="0" url={toPrivacyPolicyPage()} external inheritSize>
							placeholder
						</LinkUI>,
						<LinkUI key="1" url={toTermsOfUsePage()} external inheritSize>
							placeholder
						</LinkUI>,
					]}
				/>
			</Text>
		),
		[t]
	);

	const schema = useMemo(() => passwordFormSchema(t), [t]);

	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		trigger,
		watch,
	} = useForm<PasswordFormData>({
		resolver: joiResolver(schema),
		defaultValues: { email: defaultEmail, password: '' },
	});

	const emailFromForm = watch('email');

	const handleFormSubmit = handleSubmit((data) => {
		mutation.mutate(
			{
				username: data.email,
				password: data.password,
				workspaceId,
			},
			{
				onSuccess: (response: AuthenticateResponse) => {
					onSuccess({
						access_token: response.access_token,
						refresh_token: response.refresh_token,
					});
				},
				onError: (err) => {
					setError('root', {
						type: 'manual',
						message: getErrorMessage(err),
					});
				},
			}
		);
	});

	const onSubmit = (e: FormEvent<HTMLFormElement>) => {
		void handleFormSubmit(e);
	};

	const handleForgotPasswordClick = useCallback(
		(e: MouseEvent<HTMLAnchorElement>) => {
			if (!forgotPasswordOnClick) return;
			e.preventDefault();
			void (async () => {
				const valid = await trigger('email');
				if (valid) forgotPasswordOnClick(emailFromForm);
			})();
		},
		[forgotPasswordOnClick, trigger, emailFromForm]
	);

	const errorMessage = errors.root?.message ?? (mutation.isError ? getErrorMessage(mutation.error ?? null) : null);

	const forgotPasswordLabel = useMemo(
		() => (
			<Stack justify="between" tw="w-full">
				<TextStyle variation="normal">{t('password_step.password')}</TextStyle>
				{forgotPasswordOnClick ? (
					<a
						href={forgotPasswordHref}
						rel="noopener noreferrer"
						onClick={handleForgotPasswordClick}
						tabIndex={0}
					>
						<TextStyle variation="normal" tw="text-indigo-500">
							{t('password_step.forgot_password')}
						</TextStyle>
					</a>
				) : (
					<a href={forgotPasswordHref} rel="noopener noreferrer">
						<TextStyle variation="normal" tw="text-indigo-500">
							{t('password_step.forgot_password')}
						</TextStyle>
					</a>
				)}
			</Stack>
		),
		[t, forgotPasswordHref, forgotPasswordOnClick, handleForgotPasswordClick]
	);

	return (
		<Form onSubmit={onSubmit}>
			<Stack vertical spacing="loose">
				{errorMessage && <Banner variation="error">{errorMessage}</Banner>}
				{showEmailField && (
					<Input
						{...register('email')}
						autoCapitalize="off"
						autoComplete="email"
						autoCorrect="off"
						spellCheck="false"
						error={errors.email?.message}
						readOnly={submitting}
						type="email"
						tabIndex={0}
						label={t('password_step.email', { defaultValue: 'Email address' })}
					/>
				)}
				<Input
					{...register('password')}
					autoComplete="current-password"
					error={errors.password?.message}
					readOnly={submitting}
					type="password"
					tabIndex={0}
					label={forgotPasswordLabel}
					prefix={<Icon source={LockClosed} tw="text-gray-400" />}
				/>
				{acceptTermsBlock}
				<Stack vertical spacing="tight">
					<Button disabled={submitting} loading={submitting} size="medium" type="submit">
						{t('password_step.login')}
					</Button>
					{renderAfterSubmit}
				</Stack>
			</Stack>
		</Form>
	);
};
